import { useState, useRef, useEffect } from 'react'
import { InView } from 'react-intersection-observer'

import { Text } from '@wartek-id/text'
import { Avatar } from '@wartek-id/avatar'
import { Shimmer } from '@wartek-id/shimmer'

import RateWrapper from '../components/RateWrapper'
import { humanizeDate } from 'utils/datetime'
import { useAPIGetCeritaPraktikById } from 'api/cerita-praktik/useAPICeritaPraktik'
import { HTML_TAG_REGEX_HTML, WORD_REGEX } from 'utils/regex'
import { useSETrackEvent } from '../hooks/useSETrackEvent'

import type { ICeritaPraktikItemResponse } from 'types/cerita-praktik'

interface DetailProps {
  id: string
  onPageLoaded: (title: string) => void
}

function Detail({ id, onPageLoaded }: DetailProps) {
  const trackerPixelTimer = useRef<ReturnType<typeof setTimeout>>(null)
  const [shouldTrackPixel, setShouldTrackPixel] = useState<boolean>(false)
  const { SETrackOpenCeritaPraktikPage, SETrackPassPixel, setTrackerParams } =
    useSETrackEvent()

  const wordCount = (data: string) => {
    if (!data) return 0
    return (data.replace(HTML_TAG_REGEX_HTML, ' ').match(WORD_REGEX) || '')
      .length
  }

  const [detailData, setDetailData] = useState<ICeritaPraktikItemResponse>({
    id: '',
    revision: '',
    title: '',
    description: '',
    category: '',
    writers: [],
    sections: [],
  })

  const { isLoading } = useAPIGetCeritaPraktikById(id, {
    onSuccess: (res) => {
      /**
       * Normalizes response data:
       * 1. Omit sections that have no blocks and title
       * 2. Omit blocks that have no title, description, and attachments
       * 3. Omit attachments that have no fileUrl
       */
      const originalData = res.data
      const normalizedData = {
        ...originalData,
        writers: originalData?.writers || [],
        sections:
          originalData?.sections?.filter((section) => {
            const normalizedBlocks =
              section?.blocks.filter((block) => {
                const normalizedAttachments =
                  block.attachments?.filter((attachment) => {
                    // 3. Omit attachments that have no fileUrl
                    return attachment.fileUrl !== ''
                  }) || []

                // 2. Omit blocks that have no title, description, and attachments
                return (
                  block.title ||
                  block.description ||
                  normalizedAttachments.length > 0
                )
              }) || []

            // 1. Omit sections that have no blocks and title
            return normalizedBlocks.length > 0 || !!originalData.title
          }) || [],
      }

      // Add a blockOrder for each block
      let order = 0
      normalizedData.sections.forEach(({ blocks }) =>
        blocks.forEach((block) => {
          order += 1
          block.blockOrder = order
        })
      )

      // Set the detail state
      setDetailData(normalizedData)

      // Extract tracker metadata and set the value to the store
      setTrackerParams({
        contentId: id,
        revisionId: normalizedData.revision,
        creatorEmails: normalizedData.writers
          .map(({ email }) => email)
          .join(','),
        numOfSections: normalizedData.sections?.length || 0,
        numOfBlocks: normalizedData.sections?.reduce(
          (prev, current) => prev + (current.blocks?.length || 0),
          0
        ),
        numOfWords:
          normalizedData.sections?.reduce((prev, section) => {
            const t = section?.blocks?.reduce((prev2, block) => {
              const title = wordCount(block?.title || '')
              const description = wordCount(block?.description || '')
              return prev2 + title + description
            }, 0)

            return prev + t + wordCount(section?.title || '')
          }, 0) +
          wordCount(normalizedData?.title || '') +
          wordCount(normalizedData?.description || ''),
        numOfImages: normalizedData?.sections?.reduce((prev, section) => {
          const t = section?.blocks?.reduce((prev2, block) => {
            const attachments =
              block?.attachments.filter((attachment) => {
                return attachment.fileUrl !== ''
              }) || []
            return prev2 + attachments.length
          }, 0)

          return prev + t
        }, 0),

        category: normalizedData.category || '', // TODO: add category when ready
      })

      // Track open page
      SETrackOpenCeritaPraktikPage({
        category: normalizedData.category || '', // TODO: add category when ready
        contentId: id,
        revisionId: normalizedData.revision || '',
      })

      // Pass title to the parent component
      onPageLoaded(normalizedData.title)

      /**
       * Set `shouldTrackPixel` to true after some delay to prevent the pixel tracker to be triggered unnecessarily.
       * This way, we **assumes** that all DOM elements, including the images, are rendered and loaded to the screen.
       */
      trackerPixelTimer.current = setTimeout(() => {
        setShouldTrackPixel(true)
      }, 2000)
    },
  })

  // Clear the trackerPixel timer upon component's unmount
  useEffect(() => {
    return () => {
      if (trackerPixelTimer.current) {
        clearTimeout(trackerPixelTimer.current)
      }
    }
  }, [])

  if (isLoading) {
    return <DetailShimmer />
  }

  return (
    <main className="space-y-4 pb-6 break-words">
      {/* Header */}
      <section className="px-4 pb-4 pt-3 bg-white">
        <Text as="h1" className="mb-2" variant="heading-md">
          {detailData.title}
        </Text>
        <Text as="h2" className="italic !font-medium mb-4" variant="body">
          {detailData.description}
        </Text>

        <div className="space-y-2">
          {detailData.writers.map((writer, index) => {
            const displayName = writer.name || writer.email

            // Note: avoid using <Link> to trigger Apps deeplink
            return (
              <a
                href={`/profil/${writer.id}?from=/cerita-praktik`}
                key={`${index}-${writer.name}-${writer.id}`}
                aria-label={`Lihat profil ${displayName}`}
                className="flex items-center space-x-2"
              >
                <Avatar
                  alt={''}
                  className="flex-shrink-0"
                  name={displayName}
                  shape="circle"
                  size="sm"
                  src={writer.imageUrl}
                />
                <div className="flex flex-col">
                  <Text className="line-clamp-2" variant="helper">
                    {displayName}
                  </Text>
                  <Text className="line-clamp-2" variant="helper">
                    {humanizeDate(detailData.createdAt)}
                  </Text>
                </div>
              </a>
            )
          })}
        </div>
      </section>

      {/* Sections */}
      <section className="space-y-4 mx-2">
        {detailData.sections.map(({ title, blocks }, i) => (
          <section key={`${title}-${i}`}>
            <Text as="h3" className="px-2 mb-2" variant="heading-md">
              {title}
            </Text>

            {/* Blocks */}
            <div className="space-y-2">
              {blocks.map((block) => (
                <InView
                  as="article"
                  key={`${block.title}-${block.blockOrder}`}
                  triggerOnce
                  skip={!shouldTrackPixel}
                  threshold={0.5}
                  onChange={(inView) => {
                    if (inView) {
                      SETrackPassPixel({ blockOrder: block.blockOrder })
                    }
                  }}
                >
                  {block.attachments && block.attachments[0] && (
                    <div className="rounded-t-lg w-full overflow-hidden">
                      <img
                        className="w-full"
                        alt=""
                        src={block.attachments[0].fileUrl}
                      />
                    </div>
                  )}
                  <div className="bg-white p-3 first:rounded-t-lg last:rounded-b-lg">
                    <Text as="h4" className="mb-2" variant="heading-sm">
                      {block.title}
                    </Text>

                    {block.useHtmlEditor ? (
                      <Text
                        as="div"
                        className="simple-wysiwyg-content cerita-praktik"
                        dangerouslySetInnerHTML={{
                          __html: block.description,
                        }}
                        variant="body"
                      />
                    ) : (
                      <Text variant="body"> {block.description}</Text>
                    )}
                  </div>
                </InView>
              ))}
            </div>
          </section>
        ))}
      </section>

      <RateWrapper id={id} rating={detailData?.rating} />
    </main>
  )
}

export default Detail

function DetailShimmer() {
  return (
    <main className="space-y-4 pb-6">
      {/* Header */}
      <section className="px-4 pb-4 pt-3 bg-white rounded space-y-3">
        {/* Title */}
        <Shimmer className="mb-2" height={24} style={{ width: '100%' }} />

        {/* Description */}
        <Shimmer className="mb-4" height={50} style={{ width: '100%' }} />

        {/* Writers */}
        <div className="space-y-2">
          {[...Array(3)].map((i, index) => (
            <div
              className="flex items-center gap-2"
              key={`shimmer-umum-${index}`}
            >
              <Shimmer height={24} width={24} rounded />
              <Shimmer height={16} style={{ width: `${25 * (index + 1)}%` }} />
            </div>
          ))}
        </div>
      </section>

      {/* Section */}
      <section className="space-y-3 mx-2">
        {[...Array(2)].map((i, index) => (
          <div key={`shimmer-sections-${index}`}>
            <Shimmer className="mb-2" height={24} style={{ width: '35%' }} />

            <div className="p-4 bg-white rounded space-y-3">
              {/* Title */}
              <Shimmer className="mb-2" height={24} style={{ width: '50%' }} />

              {/* Description */}
              <Shimmer height={150} style={{ width: '100%' }} />
            </div>
          </div>
        ))}
      </section>
    </main>
  )
}
